function isReactCode(code: string, language?: string) {
  if (language) {
    const lang = language.toLowerCase();
    if (lang === 'jsx' || lang === 'tsx' || lang.includes('react')) return true;
  }
  // Strong, unambiguous React signals only.
  return (
    /^\s*import\s+React\b/m.test(code) ||
    /\bfrom\s+['"]react['"]/.test(code) ||
    /\bReactDOM\.(?:render|createRoot)\b/.test(code) ||
    /\buseState\s*\(/.test(code) ||
    /\buseEffect\s*\(/.test(code)
  );
}

function isHtmlDocument(code: string) {
  const trimmed = code.trim().toLowerCase();
  return trimmed.startsWith('<!doctype html') || trimmed.startsWith('<html');
}

export function isPreviewable(code: string, language?: string) {
  if (!code) return false;
  const lang = language?.toLowerCase() ?? '';
  if (lang === 'html' || lang.includes('html')) return true;
  if (isHtmlDocument(code)) return true;
  if (isReactCode(code, language)) return true;
  return false;
}

function escapeHtml(unsafe: string) {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function findReactRootName(code: string): string | null {
  const exportFn = code.match(/export\s+default\s+function\s+(\w+)/);
  if (exportFn) return exportFn[1];

  const exportIdent = code.match(/export\s+default\s+(\w+)/);
  if (exportIdent) return exportIdent[1];

  const fnDecl = code.match(/function\s+(\w+)\s*\(/);
  if (fnDecl) return fnDecl[1];

  const arrowConst = code.match(/const\s+(\w+)\s*=\s*\([^)]*\)\s*=>/);
  if (arrowConst) return arrowConst[1];

  return null;
}

function buildReactDocument(code: string) {
  let processed = code
    .replace(/export\s+default\s+function\s+(\w+)/, 'function $1')
    .replace(/export\s+default\s+\w+\s*;?/, '');

  const rootName = findReactRootName(code);

  if (rootName) {
    processed += `\nReactDOM.createRoot(document.getElementById('root')).render(React.createElement(${rootName}));`;
  } else if (/<[A-Za-z]/.test(processed)) {
    processed = `
      function App() {
        return (
          ${processed}
        );
      }
      ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(App));
    `;
  }

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>React Preview</title>
    <meta http-equiv="Content-Security-Policy" content="
      default-src 'self';
      script-src 'unsafe-inline' https://unpkg.com;
      style-src 'unsafe-inline' 'self' https://fonts.googleapis.com;
      font-src 'self' https://fonts.gstatic.com;
      img-src * data:;
      connect-src *;
    ">
    <style>
      html, body, #root { height: 100%; margin: 0; padding: 0; }
      body { font-family: sans-serif; background: #fff; }
    </style>
    <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
    <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
    <script crossorigin src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  </head>
  <body>
    <div id="root"></div>
    <script type="text/babel" data-presets="env,react">
${processed}
    </script>
  </body>
</html>`;
}

function getIframeSrcDocForCode(code: string, language?: string) {
  if (isHtmlDocument(code)) return code;

  const lang = language?.toLowerCase() ?? '';
  if (lang === 'html' || lang.includes('html')) return code;

  if (isReactCode(code, language)) return buildReactDocument(code);

  return `<!DOCTYPE html><html><body><pre style="white-space:pre-wrap;word-break:break-word;font-family:ui-monospace,monospace;padding:12px;margin:0">${escapeHtml(code)}</pre></body></html>`;
}

// In-memory polyfill for localStorage/sessionStorage/document.cookie. The
// iframe runs without `allow-same-origin` (combining it with `allow-scripts`
// would defeat the sandbox), so the real storage APIs throw SecurityError.
// We replace them with per-session in-memory shims so typical demo code that
// reads/writes storage doesn't crash on first access.
const STORAGE_SHIM_SCRIPT = `
<script>
  (function() {
    function makeMemoryStorage() {
      var data = Object.create(null);
      return {
        get length() { return Object.keys(data).length; },
        key: function(i) { return Object.keys(data)[i] || null; },
        getItem: function(k) { return Object.prototype.hasOwnProperty.call(data, k) ? data[k] : null; },
        setItem: function(k, v) { data[String(k)] = String(v); },
        removeItem: function(k) { delete data[String(k)]; },
        clear: function() { data = Object.create(null); },
      };
    }
    function install(name) {
      try { window[name].length; return; } catch (_) {}
      try {
        Object.defineProperty(window, name, {
          value: makeMemoryStorage(), configurable: true, writable: false,
        });
      } catch (_) {}
    }
    install('localStorage');
    install('sessionStorage');
    try { document.cookie; } catch (_) {
      try {
        var jar = '';
        Object.defineProperty(document, 'cookie', {
          configurable: true,
          get: function() { return jar; },
          set: function(v) { jar = String(v); },
        });
      } catch (_) {}
    }
  })();
</script>
`;

// All link/form navigation in a sandboxed srcDoc preview is broken: relative
// hrefs resolve against the parent origin, and the iframe's null origin can't
// navigate there ("Unsafe attempt to load URL") — and even if it could, it'd
// load the host app inside the preview, not a "next page" of the demo. So we
// suppress every navigating click/submit and surface a small toast inside the
// preview explaining what happened. In-page fragments and the special schemes
// (javascript:/mailto:/tel:/sms:/data:/blob:) pass through.
const INTERCEPT_SCRIPT = `
<script>
  (function() {
    function isInPageHash(rawHref) {
      return typeof rawHref === 'string' && rawHref.charAt(0) === '#';
    }
    function isSpecialScheme(rawHref) {
      return /^(javascript:|mailto:|tel:|sms:|data:|blob:)/i.test(rawHref || '');
    }

    var toastEl = null, toastTimer = null;
    function ensureToast() {
      if (toastEl && toastEl.isConnected) return toastEl;
      var style = document.createElement('style');
      style.textContent =
        '[data-preview-toast]{position:fixed;left:50%;bottom:16px;transform:translate(-50%,8px);' +
        'max-width:min(520px,calc(100% - 32px));padding:10px 14px;border-radius:10px;' +
        'background:rgba(20,20,22,0.94);color:#fff;font:500 13px/1.45 ui-sans-serif,system-ui,-apple-system,Segoe UI,sans-serif;' +
        'box-shadow:0 8px 28px rgba(0,0,0,0.35),0 0 0 1px rgba(255,255,255,0.06);' +
        'opacity:0;transition:opacity .18s ease,transform .18s ease;z-index:2147483647;' +
        'display:flex;gap:10px;align-items:flex-start;pointer-events:auto;cursor:pointer;}' +
        '[data-preview-toast].show{opacity:1;transform:translate(-50%,0);}' +
        '[data-preview-toast] code{font:500 12px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace;' +
        'background:rgba(255,255,255,0.10);padding:1px 6px;border-radius:6px;' +
        'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;display:inline-block;vertical-align:bottom;}' +
        '[data-preview-toast] strong{display:block;font-weight:600;margin-bottom:2px;}' +
        '[data-preview-toast] span.hint{display:block;color:rgba(255,255,255,0.65);font-weight:400;margin-top:2px;font-size:12px;}';
      document.head && document.head.appendChild(style);

      toastEl = document.createElement('div');
      toastEl.setAttribute('data-preview-toast', '');
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.addEventListener('click', hideToast);
      document.body.appendChild(toastEl);
      return toastEl;
    }
    function showToast(url, kind) {
      var el = ensureToast();
      var label = kind === 'form-submit' ? 'Form submission' : 'Navigation';
      var safe = String(url || '').replace(/[<&>"]/g, function(c) {
        return { '<':'&lt;','&':'&amp;','>':'&gt;','"':'&quot;' }[c];
      });
      el.innerHTML =
        '<div style="flex:1;min-width:0;">' +
          '<strong>' + label + ' isn\\'t supported in preview</strong>' +
          '<code>' + safe + '</code>' +
          '<span class="hint">Tap to dismiss</span>' +
        '</div>';
      requestAnimationFrame(function() { el.classList.add('show'); });
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(hideToast, 3500);
    }
    function hideToast() {
      if (!toastEl) return;
      toastEl.classList.remove('show');
      if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
    }

    document.addEventListener('click', function(e) {
      var el = e.target;
      while (el && el.tagName !== 'A') el = el.parentElement;
      if (!el || el.tagName !== 'A') return;
      var raw = el.getAttribute('href');
      if (!raw) return;
      if (isInPageHash(raw) || isSpecialScheme(raw)) return;
      e.preventDefault();
      showToast(el.href || raw, 'anchor-click');
    }, true);

    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action');
      if (!action || isInPageHash(action) || isSpecialScheme(action)) return;
      e.preventDefault();
      showToast(form.action || action, 'form-submit');
    }, true);
  })();
</script>
`;

export function getSafeIframeSrcDocForCode(code: string, language?: string) {
  const srcDoc = getIframeSrcDocForCode(code, language);
  // Storage shim must run BEFORE user code, so inject at top of <head> or
  // prepend if there is no <head>. Intercept script can stay at end.
  let withShim: string;
  if (/<head[^>]*>/i.test(srcDoc)) {
    withShim = srcDoc.replace(/<head[^>]*>/i, (m) => `${m}${STORAGE_SHIM_SCRIPT}`);
  } else if (/<html[^>]*>/i.test(srcDoc)) {
    withShim = srcDoc.replace(/<html[^>]*>/i, (m) => `${m}<head>${STORAGE_SHIM_SCRIPT}</head>`);
  } else {
    withShim = STORAGE_SHIM_SCRIPT + srcDoc;
  }

  if (withShim.includes('</body>')) {
    return withShim.replace('</body>', `${INTERCEPT_SCRIPT}</body>`);
  }
  return withShim + INTERCEPT_SCRIPT;
}
